import { useEffect, useState } from 'react';

import type { TimeOfDayPeriod } from './timeOfDayGradient';

import {
  resolveTimeOfDayPeriod,
  TIME_OF_DAY_THEME_COLOR,
} from './timeOfDayGradient';

const RECHECK_INTERVAL_MS = 60_000;

interface UseTimeOfDayGradientResult {
  /** `override` when set (demo switcher), otherwise the visitor's local time. */
  period: TimeOfDayPeriod;
  /** Demo-only: lets a fixed switcher force a period regardless of local time. */
  override: TimeOfDayPeriod | null;
  setOverride: (period: TimeOfDayPeriod | null) => void;
}

/**
 * Drives the brand gradient's Morning/Day/Night period. Re-derives from the
 * clock every minute so a page left open across a boundary (e.g. sitting
 * open right at 18:00) still transitions without a reload.
 */
export function useTimeOfDayGradient(): UseTimeOfDayGradientResult {
  const [auto, setAuto] = useState<TimeOfDayPeriod>(() =>
    resolveTimeOfDayPeriod(),
  );
  const [override, setOverride] = useState<TimeOfDayPeriod | null>(null);

  useEffect(() => {
    const id = setInterval(() => {
      setAuto(resolveTimeOfDayPeriod());
    }, RECHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const period = override ?? auto;

  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', TIME_OF_DAY_THEME_COLOR[period]);
  }, [period]);

  return { period, override, setOverride };
}
